import {
  LayoutDashboard,
  Building2,
  DoorOpen,
  ScrollText,
  ClipboardCheck,
  Receipt,
  Menu,
  BarChart3,
  MessageSquare,
  Mail,
  Settings,
  ArrowDownToLine,
  LifeBuoy,
  Wrench,
  Home,
  FlaskConical,
  AlertCircle,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

export interface TabItem {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string | null; // null = opens MoreMenu
}

export interface ExtraNavBadge {
  key: string;
  unit: string;
  tone: 'required' | 'recommended';
}

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  badgeKey?: string; // key for dynamic badge counts
  // When present, the nav badge is the sum of these badge keys (non-double-counting
  // hierarchy) instead of a single value read straight off badgeKey (leasing-support/BR-38).
  childBadgeKeys?: string[];
  // The noun the accessible label uses for this item's count ("N <badgeUnit>"). Defaults to
  // 'unread' when omitted — correct for Chat, but NOT for a count whose meaning isn't read-state
  // (Attention's is urgency; Leasing's is a hierarchy of waiting items, BR-38). Set explicitly
  // whenever badgeKey/childBadgeKeys' count isn't literally "unread".
  badgeUnit?: string;
  // Additional count badges rendered after the main badge, each read off `badges[key]`.
  // `tone` picks the colour: 'required' = amber, 'recommended' = muted.
  extraBadges?: ExtraNavBadge[];
  featureKey?: string; // feature gate key; item is hidden when feature is absent
  // Hidden in production only (staging/dev/test still show it). Backed by the
  // real backend NODE_ENV via useIsProductionEnvironment — NOT import.meta.env.DEV,
  // which is false in every deployed bundle (staging included), since staging ships
  // the same production-mode Vite build as production.
  hiddenInProduction?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Bottom tab bar items (mobile) */
export const bottomTabs: TabItem[] = [
  { key: 'dashboard', label: 'Home',     icon: LayoutDashboard, path: '/dashboard' },
  { key: 'units',     label: 'Units',    icon: DoorOpen,        path: '/units' },
  { key: 'leases',    label: 'Leases',   icon: ScrollText,      path: '/leases' },
  { key: 'expenses',  label: 'Expenses', icon: Receipt,         path: '/expenses' },
  { key: 'more',      label: 'More',     icon: Menu,            path: null },
];

/** Grouped navigation for "More" menu and desktop sidebar */
export const navGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard',  path: '/dashboard',  icon: LayoutDashboard },
      // Badge is a projection of the same attention-engine item set the dashboard bands render
      // (BR-23) — never a separately computed count. See AppShell's `badges` map.
      { label: 'Attention',  path: '/attention',  icon: AlertCircle, badgeKey: 'attentionCount', badgeUnit: 'urgent', extraBadges: [{ key: 'attentionRequiredCount', unit: 'required', tone: 'required' }, { key: 'attentionRecommendedCount', unit: 'recommended', tone: 'recommended' }] },
    ],
  },
  {
    label: 'Portfolio',
    items: [
      { label: 'Properties', path: '/properties', icon: Building2 },
      { label: 'Units',      path: '/units',      icon: DoorOpen },
      { label: 'Leases',     path: '/leases',     icon: ScrollText },
      { label: 'Checklists', path: '/checklists', icon: ClipboardCheck },
      { label: 'Rent Increases', path: '/rent-increases', icon: TrendingUp },
      // BR-38 — the top-level Leasing badge is the non-double-counting sum of the
      // Leads/Inbox-tab count (leasingUnrespondedCount, BR-7 — unchanged) and the
      // Applications-tab count (leasingUnreviewedApplicationsCount). AppShell computes
      // leasingTotalCount as their sum; do not repurpose leasingUnrespondedCount's meaning.
      { label: 'Leasing',    path: '/leasing',           icon: Home,   featureKey: 'leasing_pipeline', badgeKey: 'leasingTotalCount', childBadgeKeys: ['leasingUnrespondedCount', 'leasingUnreviewedApplicationsCount'], badgeUnit: 'waiting for you' },
      { label: 'Repairs',    path: '/repairs',    icon: Wrench,    featureKey: 'agentic_support' },
    ],
  },
  {
    label: 'Financials',
    items: [
      // R-EXT-PAY-BADGE — section badge per ADR-AT-009, computed from external-payments'
      // own data (unmatched incoming transaction count), never derived from AttentionItems.
      { label: 'External Payments', path: '/external-payments', icon: ArrowDownToLine, badgeKey: 'externalPaymentsUnmatchedCount', badgeUnit: 'unmatched' },
      { label: 'Expenses',          path: '/expenses',           icon: Receipt },
      { label: 'Reports',           path: '/reports',            icon: BarChart3 },
    ],
  },
  {
    label: 'Communication',
    items: [
      { label: 'Chat',    path: '/chat',    icon: MessageSquare, badgeKey: 'chatUnreadCount' },
      { label: 'Email',   path: '/email',   icon: Mail },
      { label: 'Simulator', path: '/relay/simulator', icon: FlaskConical, featureKey: 'agentic_support', hiddenInProduction: true },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Settings', path: '/settings', icon: Settings },
      { label: 'Support',  path: '/support',  icon: LifeBuoy },
    ],
  },
];

/**
 * Items shown in the "More" menu (excludes items already in bottom tabs).
 * We keep Dashboard in the sidebar but exclude it from More since it's a tab.
 */
export const moreMenuGroups: NavGroup[] = navGroups
  .map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        !bottomTabs.some(
          (tab) => tab.path !== null && tab.path === item.path,
        ),
    ),
  }))
  .filter((group) => group.items.length > 0);
